import React, { useEffect, useState } from 'react';
import api from '../api';
import { FaExclamationTriangle, FaBarcode, FaChartLine, FaBox } from 'react-icons/fa';

const Alerts = () => {
    const [alertas, setAlertas] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [viewMode, setViewMode] = useState('cards'); // 'cards' ou 'table'
    const [isMobile, setIsMobile] = useState(false);
    const [filtroStatus, setFiltroStatus] = useState('todos'); // 'todos', 'esgotado', 'baixo'

    // Detectar se é mobile
    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth <= 768);
            if (window.innerWidth <= 768) {
                setViewMode('cards');
            } else {
                setViewMode('table');
            }
        };

        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    useEffect(() => {
        async function fetchAlertas() {
            try {
                setLoading(true);
                const response = await api.get('/alertas');
                setAlertas(response.data);
                setError(null);
            } catch (error) {
                console.error("Erro ao buscar alertas de estoque:", error);
                setError("Erro ao carregar alertas de estoque");
            } finally {
                setLoading(false);
            }
        }
        fetchAlertas();
    }, []);

    // Componente Card para Produto
    const ProductCard = ({ produto }) => {
        const isOutOfStock = produto.estoque_atual === 0;
        const deficit = produto.estoque_minimo - produto.estoque_atual;

        return (
            <div className="bg-white rounded-lg border border-red-200 p-4 mb-4 shadow-sm">
                <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                            <FaExclamationTriangle className="text-red-500" size={16} />
                            <h3 className="font-semibold text-gray-800">{produto.nome}</h3>
                        </div>
                        <div className="flex items-center gap-1 text-sm text-gray-500">
                            <FaBarcode size={12} />
                            <span>{produto.codigo_barras}</span>
                        </div>
                    </div>
                    <div className={`px-3 py-1 rounded-full text-xs font-medium ${
                        isOutOfStock ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'
                    }`}>
                        {isOutOfStock ? 'Esgotado' : 'Baixo'}
                    </div>
                </div>

                <div className="grid grid-cols-2 gap-4 text-sm">
                    <div className="bg-red-50 rounded-lg p-3">
                        <div className="flex items-center gap-1 text-gray-500 mb-1">
                            <FaBox size={12} />
                            <span>Estoque Atual</span>
                        </div>
                        <div className="text-red-600 font-bold text-lg">{produto.estoque_atual}</div>
                    </div>
                    
                    <div className="bg-gray-50 rounded-lg p-3">
                        <div className="flex items-center gap-1 text-gray-500 mb-1">
                            <FaChartLine size={12} />
                            <span>Estoque Mínimo</span>
                        </div>
                        <div className="text-gray-800 font-bold text-lg">{produto.estoque_minimo}</div>
                    </div>
                </div>

                {deficit > 0 && (
                    <div className="mt-3 pt-3 border-t border-gray-200">
                        <div className="flex items-center justify-between text-sm">
                            <span className="text-gray-500">Necessário para estoque mínimo:</span>
                            <span className="text-red-600 font-medium">+{deficit} unidades</span>
                        </div>
                    </div>
                )}
            </div>
        );
    };

    if (loading) {
        return (
            <div className="max-w-full md:max-w-4xl mx-auto mt-10 bg-white p-4 md:p-8 rounded-lg shadow-lg">
                <div className="text-center py-8">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-red-500"></div>
                    <p className="mt-2 text-gray-600">Carregando alertas...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="max-w-full md:max-w-4xl mx-auto mt-10 bg-white p-4 md:p-8 rounded-lg shadow-lg">
                <div className="text-center py-8">
                    <FaExclamationTriangle className="mx-auto text-red-500 mb-2" size={32} />
                    <p className="text-red-600 font-medium">{error}</p>
                    <button 
                        onClick={() => window.location.reload()}
                        className="mt-4 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
                    >
                        Tentar Novamente
                    </button>
                </div>
            </div>
        );
    }

    const produtosEsgotados = alertas.filter(p => p.estoque_atual === 0);
    const produtosBaixo = alertas.filter(p => p.estoque_atual > 0 && p.estoque_atual <= p.estoque_minimo);

    // Filtrar produtos baseado no status selecionado
    let produtosFiltrados = alertas;
    if (filtroStatus === 'esgotado') {
        produtosFiltrados = produtosEsgotados;
    } else if (filtroStatus === 'baixo') {
        produtosFiltrados = produtosBaixo;
    }

    // Reorganizar produtos filtrados por categoria
    const produtosEsgotadosFiltrados = produtosFiltrados.filter(p => p.estoque_atual === 0);
    const produtosBaixoFiltrados = produtosFiltrados.filter(p => p.estoque_atual > 0 && p.estoque_atual <= p.estoque_minimo);

    return (
        <div className="max-w-full md:max-w-4xl mx-auto mt-10 bg-white p-4 md:p-8 rounded-lg shadow-lg">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
                <div>
                    <h2 className="text-xl md:text-2xl font-semibold text-gray-700 mb-2 md:mb-0">
                        Produtos com Estoque Crítico
                    </h2>
                    <div className="flex flex-wrap gap-2 text-sm text-gray-600">
                        <button
                            onClick={() => setFiltroStatus('todos')}
                            className={`px-3 py-1 rounded transition-colors ${
                                filtroStatus === 'todos' 
                                    ? 'bg-blue-500 text-white' 
                                    : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                            }`}
                        >
                            Todos ({alertas.length})
                        </button>
                        
                        {produtosEsgotados.length > 0 && (
                            <button
                                onClick={() => setFiltroStatus('esgotado')}
                                className={`px-3 py-1 rounded transition-colors ${
                                    filtroStatus === 'esgotado' 
                                        ? 'bg-red-500 text-white' 
                                        : 'bg-red-100 text-red-700 hover:bg-red-200'
                                }`}
                            >
                                {produtosEsgotados.length} esgotado{produtosEsgotados.length > 1 ? 's' : ''}
                            </button>
                        )}
                        
                        {produtosBaixo.length > 0 && (
                            <button
                                onClick={() => setFiltroStatus('baixo')}
                                className={`px-3 py-1 rounded transition-colors ${
                                    filtroStatus === 'baixo' 
                                        ? 'bg-yellow-500 text-white' 
                                        : 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
                                }`}
                            >
                                {produtosBaixo.length} com estoque baixo
                            </button>
                        )}
                    </div>
                </div>

                {!isMobile && (
                    <div className="flex gap-2 mt-4 md:mt-0">
                        <button
                            onClick={() => setViewMode('table')}
                            className={`px-4 py-2 rounded-lg transition-colors ${viewMode === 'table' ? 'bg-red-500 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                        >
                            Tabela
                        </button>
                        <button
                            onClick={() => setViewMode('cards')}
                            className={`px-4 py-2 rounded-lg transition-colors ${viewMode === 'cards' ? 'bg-red-500 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                        >
                            Cards
                        </button>
                    </div>
                )}
            </div>

            {/* Indicador de filtro ativo */}
            {filtroStatus !== 'todos' && (
                <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-center justify-between">
                        <p className="text-blue-700 text-sm font-medium">
                            Mostrando apenas: {filtroStatus === 'esgotado' ? 'Produtos Esgotados' : 'Produtos com Estoque Baixo'}
                        </p>
                        <button
                            onClick={() => setFiltroStatus('todos')}
                            className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                        >
                            Ver todos
                        </button>
                    </div>
                </div>
            )}

            {alertas.length === 0 ? (
                <div className="text-center py-8">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
                        <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                    </div>
                    <p className="text-green-600 font-medium">Nenhum produto com estoque crítico no momento.</p>
                    <p className="text-sm text-gray-500 mt-1">Todos os produtos estão com estoque adequado.</p>
                </div>
            ) : produtosFiltrados.length === 0 ? (
                <div className="text-center py-8">
                    <FaExclamationTriangle className="mx-auto text-gray-400 mb-2" size={24} />
                    <p className="text-gray-600 font-medium">
                        Nenhum produto encontrado com o filtro selecionado.
                    </p>
                    <button
                        onClick={() => setFiltroStatus('todos')}
                        className="mt-3 text-blue-600 hover:text-blue-800 text-sm"
                    >
                        Ver todos os produtos
                    </button>
                </div>
            ) : viewMode === 'table' ? (
                <div className="overflow-x-auto -mx-4 md:mx-0">
                    <div className="inline-block min-w-full align-middle px-4 md:px-0">
                        <table className="min-w-full border-collapse border border-gray-300">
                            <thead>
                                <tr style={{ backgroundColor: "#5B4740", color: "#FFF7F5" }}>
                                    <th className="p-2 text-left">Nome</th>
                                    <th className="p-2 text-center md:table-cell hidden">Código de Barras</th>
                                    <th className="p-2 text-center">Atual</th>
                                    <th className="p-2 text-center">Mínimo</th>
                                    <th className="p-2 text-center md:table-cell hidden">Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {produtosFiltrados.map((produto) => (
                                    <tr key={produto.id} className="border border-gray-300 hover:bg-gray-50">
                                        <td className="p-2">
                                            <div className="flex items-center gap-2">
                                                {produto.estoque_atual === 0 ? (
                                                    <FaExclamationTriangle className="text-red-500 flex-shrink-0" size={14} />
                                                ) : (
                                                    <FaExclamationTriangle className="text-yellow-500 flex-shrink-0" size={14} />
                                                )}
                                                <span className="truncate">{produto.nome}</span>
                                            </div>
                                        </td>
                                        <td className="p-2 text-center md:table-cell hidden">{produto.codigo_barras}</td>
                                        <td className={`p-2 text-center font-bold ${
                                            produto.estoque_atual === 0 ? 'text-red-600' : 
                                            produto.estoque_atual <= produto.estoque_minimo ? 'text-yellow-600' : ''
                                        }`}>
                                            {produto.estoque_atual}
                                        </td>
                                        <td className="p-2 text-center">{produto.estoque_minimo}</td>
                                        <td className="p-2 text-center md:table-cell hidden">
                                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                                produto.estoque_atual === 0 
                                                    ? 'bg-red-100 text-red-700' 
                                                    : 'bg-yellow-100 text-yellow-700'
                                            }`}>
                                                {produto.estoque_atual === 0 ? 'Esgotado' : 'Baixo'}
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            ) : (
                <div className="space-y-4">
                    {produtosEsgotadosFiltrados.length > 0 && (
                        <div>
                            <h3 className="text-red-600 font-medium mb-3 flex items-center gap-2">
                                <FaExclamationTriangle />
                                Produtos Esgotados ({produtosEsgotadosFiltrados.length})
                            </h3>
                            {produtosEsgotadosFiltrados.map((produto) => (
                                <ProductCard key={produto.id} produto={produto} />
                            ))}
                        </div>
                    )}

                    {produtosBaixoFiltrados.length > 0 && (
                        <div>
                            <h3 className="text-yellow-600 font-medium mb-3 flex items-center gap-2">
                                <FaExclamationTriangle />
                                Produtos com Estoque Baixo ({produtosBaixoFiltrados.length})
                            </h3>
                            {produtosBaixoFiltrados.map((produto) => (
                                <ProductCard key={produto.id} produto={produto} />
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default Alerts;